import { GeneratedCollection } from "../generated.collection.abstraction";
import { TransactionView } from "../../../models/view/transaction/transaction.view";
import { computed, inject, signal, Signal, WritableSignal } from "@angular/core";
import { Observable } from "rxjs";
import { NormalizeTransaction } from "@bitwarden/web-vault/app/models/view/transaction/normalize/normalizeTransaction.utils";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PreferenceView } from "@bitwarden/web-vault/app/models/view/preference/preference.view";
import { ReferenceDataView } from "@bitwarden/web-vault/app/models/view/reference-data/reference-data.view";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { toObservable } from "@angular/core/rxjs-interop";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import {
  BALANCE_DEFINITIONS,
  TransactionStatusEnum,
} from "@bitwarden/web-vault/app/models/enum/transactionType";
import {
  createTransactionView,
  enrichTransaction,
  enrichAccountData,
} from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import { TransactionConversionService } from "@bitwarden/web-vault/app/services/DataService/transaction/transaction-conversion.service";
import { PluginConfigService } from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";

/** Records of the record ids skipped during the last generate() run, grouped by root cause,
    so consumers can detect and react to a degraded (incomplete) collection instead of relying
    solely on console warnings. */
export interface EnrichTransactionSkippedRecords {
  readonly transactionsMissingAccount: readonly string[];
  readonly transactionsMissingInstitution: readonly string[];
  readonly accountsMissingInstitution: readonly string[];
}

const emptySkippedRecords = (): EnrichTransactionSkippedRecords => ({
  transactionsMissingAccount: [],
  transactionsMissingInstitution: [],
  accountsMissingInstitution: [],
});

export class EnrichTransactionGeneratedCollection extends GeneratedCollection<TransactionView> {
  protected log: LogService = inject(LogService);
  protected userStore: UserStoreService = inject(UserStoreService);
  private transactionConversionService = inject(TransactionConversionService);
  private pluginConfigService = inject(PluginConfigService);

  protected _collection: WritableSignal<TransactionView[]> = signal<TransactionView[]>(null);
  collection: Signal<TransactionView[]> = this._collection.asReadonly();
  collection$: Observable<TransactionView[]> = toObservable(this.collection);

  /** Skipped record ids from the last generate() run. Non-empty means the collection is
      incomplete - balances/classification counts computed from it are missing data. */
  protected _skippedRecords: WritableSignal<EnrichTransactionSkippedRecords> =
    signal<EnrichTransactionSkippedRecords>(emptySkippedRecords());
  skippedRecords: Signal<EnrichTransactionSkippedRecords> = this._skippedRecords.asReadonly();

  classificationCountMap = computed(() => {
    const map = new Map<string, number>();
    for (const transaction of this.collection() ?? []) {
      for (const id of transaction.c_classifications) {
        map.set(id, (map.get(id) ?? 0) + 1);
      }
    }
    return map;
  });
  purgingAccountIds: string[] = [];

  generate(
    preference: PreferenceView,
    referenceData: ReferenceDataView[],
    transactions: TransactionView[],
    accounts: AccountView[],
  ): void {
    this.log.info("---- Regenerating Enrich Transaction Collection ---- ");
    const normalizeTransaction = new NormalizeTransaction(
      this.log,
      preference.coreCurrency,
      referenceData,
    );

    /** Store views are frozen - clone first, then mutate the clones only.
        Link detection needs the full set, so it runs before the per-view
        enrichment loop freezes each clone. */
    const clones = transactions.map((transaction) => transaction.clone());
    const tcOptions = this.pluginConfigService.getTransferConversionOptions();
    // classifyExistingLinks only reflects links the user already made, so it always runs;
    // auto-detection is gated on the feature + its own switch (both default on).
    this.transactionConversionService.classifyExistingLinks(clones);
    if (tcOptions.enabled && tcOptions.autoDetectEnabled) {
      // Synthetic balance rows (opening/closing/manual/alignment/etc.) are not real transfers -
      // exclude them so they can't be matched and stamped with c_linkType alongside a genuine
      // transaction, which would wrongly pull that transaction out of the monthly summary totals.
      this.transactionConversionService.findRelatedTransaction(
        clones.filter((c) => !BALANCE_DEFINITIONS.has(c.definition)),
        tcOptions.linkWindowHours * 60 * 60 * 1000,
      );
    }

    /** Every account whose institution could not be resolved, filled by both loops so the list
        stays complete whether or not the account happened to have transactions. */
    const accountsMissingInstitution = new Set<string>();

    const {
      enrichTransactions,
      accountWithTransactions,
      transactionsMissingAccount,
      transactionsMissingInstitution,
    } = this.buildEnrichedTransactions(clones, normalizeTransaction, accountsMissingInstitution);

    const emptyBats = this.buildEmptyAccountBalances(
      accounts,
      accountWithTransactions,
      normalizeTransaction,
      accountsMissingInstitution,
    );
    enrichTransactions.push(...emptyBats);

    this._collection.set(enrichTransactions);

    const skippedRecords: EnrichTransactionSkippedRecords = {
      transactionsMissingAccount,
      transactionsMissingInstitution,
      accountsMissingInstitution: [...accountsMissingInstitution],
    };
    this._skippedRecords.set(skippedRecords);

    const totalSkipped =
      skippedRecords.transactionsMissingAccount.length +
      skippedRecords.transactionsMissingInstitution.length +
      skippedRecords.accountsMissingInstitution.length;
    if (totalSkipped > 0) {
      // One roll-up per run, in addition to the per-record warnings logged above, so the
      // aggregate picture ("N records skipped this run") doesn't get lost among individual lines.
      this.log.info(
        `Enrich Transaction generation skipped ${totalSkipped} record(s) this run - ` +
          `${skippedRecords.transactionsMissingAccount.length} transaction(s) missing account, ` +
          `${skippedRecords.transactionsMissingInstitution.length} transaction(s) missing institution, ` +
          `${skippedRecords.accountsMissingInstitution.length} account(s) missing institution. ` +
          `The resulting collection is incomplete - see skippedRecords for details.`,
      );
    }
  }

  private buildEnrichedTransactions(
    clones: TransactionView[],
    normalizeTransaction: NormalizeTransaction,
    accountsMissingInstitution: Set<string>,
  ): {
    enrichTransactions: TransactionView[];
    accountWithTransactions: Set<string>;
    transactionsMissingAccount: string[];
    transactionsMissingInstitution: string[];
  } {
    const accountWithTransactions = new Set<string>();
    const enrichTransactions: TransactionView[] = [];
    const transactionsMissingAccount: string[] = [];
    const transactionsMissingInstitution: string[] = [];

    for (const enrich of clones) {
      const transactionAccount = this.findAccount(enrich.accountLink.id);
      if (!transactionAccount) {
        this.log.warning(
          `Transaction ${enrich.id} has no account with id ${enrich.accountLink.id}`,
        );
        transactionsMissingAccount.push(enrich.id);
        continue;
      }
      const institution = this.findInstitution(transactionAccount.institutionLink.institutionId);
      if (!institution) {
        this.log.warning(
          `Transaction ${enrich.id} has no institution with id ${transactionAccount.institutionLink.institutionId}`,
        );
        transactionsMissingInstitution.push(enrich.id);
        // The account itself is real - it just can't be enriched right now. Record it so the
        // account-level list stays complete, and mark it as having transactions so
        // buildEmptyAccountBalances doesn't also warn about the same missing institution while
        // trying to synthesize a $0 balance for it.
        accountsMissingInstitution.add(transactionAccount.id);
        if (enrich.accountLink.id) {
          accountWithTransactions.add(enrich.accountLink.id);
        }
        continue;
      }
      enrichTransaction(enrich);
      enrichAccountData(transactionAccount, institution, enrich);
      normalizeTransaction.normalizeImportedTransaction(enrich);
      enrichTransactions.push(this.deepFreeze(enrich));

      if (enrich.accountLink.id) {
        accountWithTransactions.add(enrich.accountLink.id);
      }
    }

    return {
      enrichTransactions,
      accountWithTransactions,
      transactionsMissingAccount,
      transactionsMissingInstitution,
    };
  }

  /** todo @alex this is super dodgy, need to clean up **/
  /** Loop into the account list and add a 0$ BAT for accounts that has no transaction so that the ui behave liike other accounts **/
  private buildEmptyAccountBalances(
    accounts: AccountView[],
    accountWithTransactions: Set<string>,
    normalizeTransaction: NormalizeTransaction,
    accountsMissingInstitution: Set<string>,
  ): TransactionView[] {
    const emptyBats: TransactionView[] = [];

    for (const account of accounts) {
      if (accountWithTransactions.has(account.id) || this.purgingAccountIds.includes(account.id)) {
        continue;
      }
      const institution = this.findInstitution(account.institutionLink.institutionId);
      if (!institution) {
        this.log.warning(
          `Account ${account.id} has no institution with id ${account.institutionLink.institutionId}`,
        );
        accountsMissingInstitution.add(account.id);
        continue;
      }
      const emptyBat = this.getEmptyAccountBalance(account);
      enrichTransaction(emptyBat);
      enrichAccountData(account, institution, emptyBat);
      normalizeTransaction.normalizeImportedTransaction(emptyBat);
      emptyBats.push(this.deepFreeze(emptyBat));
    }

    return emptyBats;
  }

  private findAccount(id: string): AccountView | undefined {
    return this.userStore.accounts.accountViews().find((acc) => acc.id === id);
  }

  private findInstitution(id: string): InstitutionView | undefined {
    return this.userStore.institutions.institutionViews().find((inst) => inst.id === id);
  }

  private getEmptyAccountBalance(account: AccountView) {
    const emptyBalanceTransaction = createTransactionView(
      account.id,
      account.institutionLink.institutionId,
      0,
      account.currency,
      account.dateCreated,
      "No Transaction in Account",
      [], // allocations
      [], // linkedTo
      true,
      TransactionStatusEnum.empty_account_balance,
      0, // bankImportedBalance
    );
    return emptyBalanceTransaction;
  }

  update(collection: TransactionView[]): void {
    //todo implement
  }

  clear(): void {
    this._collection.set(null);
    this._skippedRecords.set(emptySkippedRecords());
  }
}
